export const RADIX_DECIMAL_BASE = 10;
export const CART_LOCAL_STORAGE_ID = 'shopping-cart-storage';
export const CHECKOUT_SESSION_STORAGE_ID = 'checkout-form-data';
export const ALWAYS_VALID_NUKI_ID = 'always-valid-nuki-id';
export const SMART_LOCK_UNKNOWN = 'SL_UNKNOWN';
export const CART_DEFAULT_TIMEOUT_IN_MINUTES = 30 * 24 * 60; // 30 days
export const MILISECONDS_IN_MINUTE = 1000 * 60;
export const DEFAULT_OUT_OF_STOCK_THRESHOLD = 0;
export const NOTIFY_ME_PORTAL_TARGET_ID = 'notify-me-portal-target';
export const CART_PORTAL_TARGET_ID = 'cart-portal-target';

export const DEFAULT_MIN_DELIVERY_TIME_FALLBACK = 3;
export const DEFAULT_MAX_DELIVERY_TIME_FALLBACK = 5;

// localization cache keys
export const LOCALIZATION_CACHE_LANGUAGE_COUNTRY_LIST_KEY = 'language-country-list';
export const LOCALIZATION_CACHE_COUNTRY_AREA_CODE_DICT_KEY = 'countries-to-area-code-dict';
export const LOCALIZATION_CACHE_AREA_CODE_ARE_VAR_DICT_KEY = 'areas-code-to-area-var-dict';

// localization headers names
export const GEO_IP_COUNTRY_CODE = 'X-GeoIP-Country-Code';
export const GEO_IP_COUNTRY_NAME = 'X-GeoIP-Country-Name';
export const GEO_IP_ENABLED = 'X-GeoIP-Enabled';

// localization cookies
export const LOCALIZATION_COOKIE_NAME = 'localization';
export const LOCALIZATION_SAVED_COOKIE_NAME = 'localization_saved';

// USA active cookie name
export const ACTIVE_US_COOKIE_NAME = 'active_us';

// Mollie
export const MOLLIE_PROFILE_ID = 'pfl_hmquWy9PFV';
export const MOLLIE_POLLING_INTERVAL = 5000; // 5 seconds
export const MOLLIE_DEFAULT_MIN_AMOUNT = 0.01;

// Ory
export const ORY_IDENTITY_METADATA_CRYSTALLIZE_ID_PATH = '/metadata_admin/crystallizeId';
export const ORY_IDENTITY_METADATA_CRYSTALLIZE_ID_NAME = 'crystallizeId';
export const ORY_IDENTITY_CUSTOMER_EMAIL_PATH = '/traits/email';

// Installation Check cookies
export const INSTALLATION_CHECK_COOKIE_NAME = 'installation-check-id';

// Opener Check cookies
export const OPENER_CHECK_COOKIE_NAME = 'opener-check-id';

// Top-banner dismissal state — a JSON array of { id, isDismissed }, readable server-side so the
// banner renders already-dismissed on SSR (no flash).
export const BANNERS_COOKIE_NAME = 'nu_banners';

export const NUKI_SESSION_COOKIE_NAME = 'nuki.sid';
export const USER_META_FIRST_COOKIE_NAME = 'um';
export const USER_META_CURRENT_COOKIE_NAME = 'umc';
export const QUALIFIED_USER_COOKIE_NAME = 'qualified-user';
export const QUALIFIED_USER_SAVED_COOKIE_NAME = 'qualified-user-saved';
export const PAGE_COUNT_COOKIE_NAME = 'page-count';

// Slug of the order-confirmation page within the region web hierarchy: EU
// `/webhierarchydraft/order-received`, US `/webhierarchyhermes/order-received` (resolved as
// `${crystallizePaths.root}/${slug}`). It is a normal page-grid page fetched by the order-received route.
export const ORDER_CONFIRMATION_PAGE_SLUG = 'order-received';

// Options must mirror how middleware.ts originally writes um/umc so subsequent
// server-action updates target the same .nuki.io cookie instead of creating a
// new host-only cookie that gets shadowed by the existing one.
export const USER_META_CURRENT_COOKIE_OPTIONS = {
  sameSite: 'none',
  secure: true,
  domain: '.nuki.io',
} as const;

export const USER_META_FIRST_COOKIE_OPTIONS = {
  ...USER_META_CURRENT_COOKIE_OPTIONS,
  maxAge: 10 * 12 * 30 * 24 * 60 * 60, // 10 years
} as const;

// Experiments (A/B testing)
export const EXPERIMENT_VAR_COOKIE_NAME = 'var';
export const EXPERIMENT_VAR_URL_PARAM = 'var';

// Host-only on purpose (no `domain`, so no `.nuki.io` shadowing — see the um/umc note above) and
// session-scoped (no `maxAge`): the assignment dies with the session. `httpOnly: false` because
// GTM/exponea read the values in the browser; the payload is a non-sensitive experiment label.
export const EXPERIMENT_VAR_COOKIE_OPTIONS = {
  sameSite: 'none',
  secure: true,
  httpOnly: false,
  path: '/',
} as const;

// NAG - Nuki Audience Group
export const CLUB_CHECKOUT_NAG_VALUE = 'club-checkout';
export const CLUB_CHOOSER_NAG_VALUE = 'pop-up-chooser-club-only';
export const CLUB_PDP_NAG_VALUE = 'pop-up-pdp-club-only';

export enum SCHEMA_ORG_TYPES {
  FAQPage = 'FAQPage',
  Product = 'Product',
}

export const NUKI_APPS_BASE_INFORMATION = {
  iosDownloadMetadata: {
    bundleId: 'E96XHKRH7E.io.nuki.ios',
    href: 'https://apps.apple.com/at/app/nuki-smart-lock/id1044998081',
    image: {
      url: 'https://media.crystallize.com/nuki-beta/24/10/11/18/app_store_badge.svg',
      key: 'ios',
    },
  },

  androidDownloadMetadata: {
    href: 'https://play.google.com/store/apps/details?id=io.nuki',
    image: {
      url: 'https://media.crystallize.com/nuki-beta/24/10/11/23/google_play_store_badge.svg',
      key: 'android',
    },
  },

  huaweiDownloadMetadata: {
    href: 'https://appgallery.huawei.com/app/C103052265',
    image: {
      url: '/images/huawei-app-gallery-badge.svg',
      key: 'huawei',
    },
  },
};

export const PAYMENT_METHOD_COUNTRIES = {
  sofort: ['ES', 'NL', 'DE', 'BE', 'AT'],
  przelewy24: ['PL'],
  giropay: ['DE'],
  eps: ['AT'],
  bancontact: ['BE'],
  ideal: ['NL'],
  mybank: ['ES', 'IT'],
  twint: ['CH'],
};

// ISO codes, as delivered by Crystallize. Normalize with `mapCountryCode` before comparing:
// the localization layer still uses the legacy 'uk' alias for 'gb'.
export const COUNTRIES_PHONE_REQUIRED = [
  'UK',
  'GB',
  'AE',
  'CA',
  'EG',
  'NO',
  'TR',
  'BA',
  'RS',
  'AD',
  'MK',
  'ME',
];
